// Blooming in the web app (docs/blooming.md): the blooming thread page (the conversation beside a live preview of the
// draft, stacked on phones), its versions, the chip under a thread's first message in the channel, the app home's
// Blooming section and "New app" dialog, and an app's About tab (owner, editors, versions, its thread).
import { useEffect, useMemo, useRef, useState } from "react";
import { INTEGRATIONS, threadStreamId, type AppManifest, type AppVersion, type BloomingThread, type BotInfo, type Member, type Role } from "@bloom/shared";
import { api } from "../api.ts";
import type { Room } from "../realtime.ts";
import { AppFrame } from "../components/AppFrame.tsx";
import type { CardHost } from "../components/AppCard.tsx";
import { CardPreviewsPanel } from "../components/CardPreviews.tsx";
import { isNarrow } from "../mobile.ts";
import { Avatar, colorFor } from "../components/ui.tsx";
import { CloseButton } from "../components/icons.tsx";
import { BotAbout } from "../components/BotSettings.tsx";
import { Confirm } from "../components/Confirm.tsx";
import "../components/apps.css";
import "../components/blooming.css";

const when = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
function ago(ts: number) {
  const s = (ts - Date.now()) / 1000;
  if (Math.abs(s) < 60) return "just now";
  if (Math.abs(s) < 3600) return rtf.format(Math.round(s / 60), "minute");
  if (Math.abs(s) < 86400) return rtf.format(Math.round(s / 3600), "hour");
  return rtf.format(Math.round(s / 86400), "day");
}

/** Who may publish, lock and restore: the thread's owner, the app's editors, the server's owners and admins. */
export function canEditThread(t: BloomingThread, me: { id: string; role: Role }, apps: AppManifest[]): boolean {
  if (t.ownerId === me.id) return true;
  // A bot is its owner's alone (docs/bots.md).
  if (t.botId) return false;
  if (me.role !== "member") return true;
  const app = t.appSlug ? apps.find((a) => a.slug === t.appSlug) : undefined;
  return !!app?.bloom?.editors.includes(me.id);
}

/** Who may delete a bloomed app: the creator (who bloomed it), a server admin, or the server owner. */
export function canDeleteApp(app: AppManifest, me: { id: string; role: Role }): boolean {
  if (!app.bloom) return false;
  return app.bloom.owner === me.id || me.role === "admin" || me.role === "owner";
}

/** Who may delete an app version: the thread creator, a server admin, or the server owner. */
export function canDeleteVersion(t: BloomingThread, me: { id: string; role: Role }): boolean {
  return t.ownerId === me.id || me.role === "admin" || me.role === "owner";
}

/**
 * What deleting version `n` would delete, or null when the room refuses it (`Blooming.deleteVersion`):
 * the actively published (live) version cannot be deleted. Non-live published versions and unpublished drafts can.
 * `live` is the live version's number: the thread's `liveVersion` on the thread page, the installed app's `version` on About.
 */
export function deleteKind(versions: Pick<AppVersion, "n" | "publishedAt">[], n: number, live: number | null): "published" | "draft" | null {
  const v = versions.find((x) => x.n === n);
  if (!v) return null;
  if (n === live) return null;
  return v.publishedAt ? "published" : "draft";
}

const DELETE_TEXT = {
  published: "This version will be deleted. It can't be published again.",
  draft: "This draft will be deleted and the preview will revert to an earlier version. Future changes will build off that earlier version.",
};

function DeleteVersionConfirm({ n, kind, onConfirm, onClose }: { n: number; kind: ReturnType<typeof deleteKind>; onConfirm: () => Promise<void>; onClose: () => void }) {
  const what = kind === "draft" ? "draft" : "version";
  return (
    <Confirm
      title={`Delete ${kind === "draft" ? "Draft" : "Version"} ${n}?`}
      confirmLabel={`Delete ${what}`}
      danger
      onConfirm={async () => {
        await onConfirm();
        return null;
      }}
      onClose={onClose}
    >
      <p className="muted">{DELETE_TEXT[kind ?? "draft"]}</p>
    </Confirm>
  );
}

const STATUS: Record<BloomingThread["status"], { label: string; tone: string }> = {
  idle: { label: "Waiting for a prompt", tone: "quiet" },
  asking: { label: "Bloom asked a question", tone: "ask" },
  building: { label: "Building", tone: "busy" },
  ready: { label: "Draft ready", tone: "ok" },
  failed: { label: "Didn't finish", tone: "bad" },
  paused: { label: "Paused: budget used up", tone: "bad" },
};

export function BloomStatus({ thread, compact }: { thread: BloomingThread; compact?: boolean }) {
  const s = STATUS[thread.status];
  const text = thread.status === "building" && thread.progress ? thread.progress : thread.status === "ready" && thread.drafts ? `Draft ${thread.drafts} ready` : s.label;
  return (
    <span className={`bloom-status ${s.tone}${compact ? " compact" : ""}`} role="status">
      <span className="bloom-status-dot" aria-hidden="true" />
      <span className={thread.status === "building" ? "shimmer" : undefined}>{text}</span>
    </span>
  );
}

/** Under a blooming thread's first message in the channel: what's growing there, and a way in. */
export function BloomChip({ thread, onOpen }: { thread: BloomingThread; onOpen: () => void }) {
  const live = thread.liveVersion !== null;
  return (
    <button type="button" className="bloom-chip" onClick={onOpen} aria-label={`Open the blooming thread for ${thread.title}`}>
      <span className="bloom-chip-icon" aria-hidden="true">{thread.icon}</span>
      <span className="bloom-chip-text">
        <span className="bloom-chip-title">
          {thread.title}
          {live && <span className="bloom-tag live">Live · v{thread.liveVersion}</span>}
        </span>
        <span className="bloom-chip-sub">
          <span className="bloom-chip-kind">🌱 Blooming thread</span>
          <span aria-hidden="true">·</span>
          <BloomStatus thread={thread} compact />
        </span>
      </span>
      <span className="bloom-chip-open">Open</span>
    </button>
  );
}

const STEPS = [
  { key: /reading|thinking/i, label: "Reading the request" },
  { key: /planning/i, label: "Planning it" },
  { key: /actions|trying/i, label: "Writing the actions" },
  { key: /view|design/i, label: "Designing the view" },
  { key: /checks|fixing/i, label: "Running checks" },
];

function stepOf(progress: string | undefined): number {
  if (!progress) return 0;
  const i = STEPS.findIndex((s) => s.key.test(progress));
  return i < 0 ? 0 : i;
}

interface ViewProps {
  thread: BloomingThread;
  room: Room;
  me: { id: string; name: string; role: Role };
  members: Member[];
  names: Map<string, string>;
  apps: AppManifest[];
  /** The conversation: the thread's messages and its composer (drawn by the server page). */
  chat: React.ReactNode;
  channelName: string;
  onBack: () => void;
  menuButton: React.ReactNode;
  openApp: (slug: string) => void;
  /** Draws app cards (the Cards view uses it over the preview's data). */
  cards?: CardHost;
  /** Puts a prompt for the build agent in the thread's composer ("Change this card"). */
  onPrompt: (text: string) => void;
}

/**
 * A blooming thread: the conversation beside a live preview of the newest draft, or its Cards view (every command's
 * chat cards); tabs on phones.
 */
export function BloomingView({ thread, room, me, members, names, apps, chat, channelName, onBack, menuButton, openApp, cards, onPrompt }: ViewProps) {
  const [tab, setTab] = useState<"chat" | "preview">("chat");
  const [pane, setPane] = useState<"app" | "cards">("app");
  const [route, setRoute] = useState<Record<string, string>>({});
  const [versions, setVersions] = useState<AppVersion[] | null>(null);
  const [showVersions, setShowVersions] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<{ text: string; error?: boolean; app?: string } | null>(null);
  const [seenDraft, setSeenDraft] = useState(thread.drafts);
  const [deleteVersionNum, setDeleteVersionNum] = useState<number | null>(null);
  const editor = canEditThread(thread, me, apps);
  const mayDeleteVersion = canDeleteVersion(thread, me);
  // A bot's thread ("Make a new bot"): only its owner sees it, so it says "bot" and has nothing to lock.
  const bot = !!thread.botId;
  const kind = bot ? "bot" : "app";
  const preview = thread.preview;
  const stream = threadStreamId(thread.id);

  const load = () => void room.bloomVersions(thread.id).then((v) => v && setVersions(v));
  useEffect(load, [thread.id, thread.drafts, thread.liveVersion]);
  useEffect(() => {
    if (tab === "preview") setSeenDraft(thread.drafts);
  }, [tab, thread.drafts]);
  useEffect(() => setNote(null), [thread.id]);
  const showInApp = (r: Record<string, string>) => {
    setRoute(r);
    setPane("app");
    setTab("preview");
  };
  const prompt = (text: string) => {
    setTab("chat");
    onPrompt(text);
  };

  const previewVersion = versions?.find((v) => v.n === preview?.version);
  const isLive = !!preview && thread.liveVersion === preview.version;
  const publish = async (n: number) => {
    setBusy(`publish:${n}`);
    setNote(null);
    const r = await room.bloomPublish(thread.id, n);
    setBusy(null);
    const manifest = versions?.find((v) => v.n === n)?.manifest;
    const slug = manifest?.slug;
    const first = bot ? `Published! Add ${manifest?.bot?.name ?? "your bot"} to channels from its About tab, then @mention it there.` : `Published! /${slug} is live for everyone.`;
    if (r.ok) setNote({ text: thread.liveVersion === null ? first : thread.liveVersion > n ? `Rolled back to version ${n}.` : `Version ${n} is live.`, app: slug });
    else setNote({ text: r.message, error: true });
    load();
  };
  const previewAt = async (n: number) => {
    setBusy(`preview:${n}`);
    const r = await room.bloomPreview(thread.id, { version: n });
    setBusy(null);
    if (!r.ok) setNote({ text: r.message, error: true });
    else setTab("preview");
  };
  const reset = async () => {
    setBusy("reset");
    const r = await room.bloomPreview(thread.id, { reset: true });
    setBusy(null);
    setNote(r.ok ? { text: thread.appSlug ? "The preview has a fresh copy of the live data." : "The preview's sample data is back." } : { text: r.message, error: true });
  };
  const lock = async () => {
    const r = await room.bloomLock(thread.id, !thread.locked);
    if (!r.ok) setNote({ text: r.message, error: true });
  };
  const deleteVersion = async (n: number) => {
    setBusy(`deleteVersion:${n}`);
    const r = await room.deleteVersion(thread.id, n);
    setBusy(null);
    if (!r.ok) setNote({ text: r.message ?? "Couldn't delete version", error: true });
    else {
      setNote({ text: `Version ${n} deleted.` });
      load();
    }
  };

  const owner = names.get(thread.ownerId) ?? "Someone";
  const step = stepOf(thread.progress);
  const building = thread.status === "building";
  const newDraft = thread.drafts > seenDraft && tab === "chat";

  return (
    <main className={`main bloom-main tab-${tab} pane-${pane}`}>
      <header className="main-head bloom-head">
        {menuButton}
        <button type="button" className="icon-btn bloom-back" onClick={onBack} aria-label={`Back to ${channelName}`} title={`Back to ${channelName}`}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m15 18-6-6 6-6" /></svg>
        </button>
        <span className="bloom-head-icon" aria-hidden="true">{thread.icon}</span>
        <div className="bloom-head-title">
          <h1>{thread.title}</h1>
          <div className="muted bloom-head-sub">
            {bot ? (
              <><span className="bloom-head-kind">Your bot's thread</span> · only you can see it</>
            ) : (
              <><span className="bloom-head-kind">Blooming thread</span> in {channelName} · started by {owner}</>
            )}
          </div>
        </div>
        <BloomStatus thread={thread} />
        {bot ? null : editor ? (
          <button type="button" className={`icon-btn bloom-lock${thread.locked ? " on" : ""}`} onClick={() => void lock()} aria-pressed={thread.locked} aria-label={thread.locked ? "Unlock the thread (anyone can prompt)" : "Lock the thread (only the owner and editors can prompt)"} title={thread.locked ? "Locked: only the owner and editors can prompt. Click to unlock." : "Anyone can prompt. Click to lock."}>
            <LockIcon locked={thread.locked} />
          </button>
        ) : (
          thread.locked && <span className="bloom-lock-badge" title="Only the app's owner and editors can prompt"><LockIcon locked /></span>
        )}
      </header>
      <div className="bloom-tabs mobile-only" role="tablist" aria-label="Blooming thread">
        <button type="button" role="tab" aria-selected={tab === "chat"} onClick={() => setTab("chat")}>Chat</button>
        <button type="button" role="tab" aria-selected={tab === "preview" && pane === "app"} onClick={() => (setTab("preview"), setPane("app"))}>
          Preview{preview ? ` · Draft ${preview.version}` : ""}
          {newDraft && <span className="bloom-dot" aria-label="new draft" />}
        </button>
        <button type="button" role="tab" aria-selected={tab === "preview" && pane === "cards"} onClick={() => (setTab("preview"), setPane("cards"))}>
          Cards
        </button>
      </div>
      <div className="bloom-split">
        <section className="bloom-chat" aria-label="Conversation">{chat}</section>
        <section className="bloom-preview" aria-label="Preview">
          <div className="bloom-preview-bar">
            <div className="bloom-pane-seg desktop-only" role="tablist" aria-label="Show">
              <button type="button" role="tab" aria-selected={pane === "app"} onClick={() => setPane("app")}>{bot ? "Bot" : "App"}</button>
              <button type="button" role="tab" aria-selected={pane === "cards"} onClick={() => setPane("cards")}>Cards</button>
            </div>
            <div className="bloom-preview-label">
              <span className="bloom-eyebrow">Preview</span>
              {preview ? (
                <>
                  <strong>Draft {preview.version}</strong>
                  {isLive && <span className="bloom-tag live">Live</span>}
                  {previewVersion && <span className="muted bloom-preview-summary" title={previewVersion.summary}>{previewVersion.summary}</span>}
                </>
              ) : (
                <span className="muted">No draft yet</span>
              )}
            </div>
            <div className="bloom-preview-actions">
              {thread.drafts > 0 && (
                <button type="button" className={`btn btn-small${showVersions ? " active" : ""}`} onClick={() => setShowVersions((v) => !v)} aria-expanded={showVersions}>
                  Versions <span className="bloom-count">{thread.drafts}</span>
                </button>
              )}
              {preview && (
                <button type="button" className="btn btn-small" onClick={() => void reset()} disabled={busy === "reset" || (thread.locked && !editor)} title={thread.appSlug ? `Copy the live ${kind}'s data into the preview again` : "Put the sample data back"}>
                  Reset data
                </button>
              )}
              {preview && (
                <button
                  type="button"
                  className="btn btn-small btn-primary"
                  onClick={() => void publish(preview.version)}
                  disabled={!editor || isLive || busy !== null}
                  title={!editor ? (bot ? "Only the bot's owner can publish" : "Only the app's owner and editors can publish") : isLive ? "This version is live" : undefined}
                >
                  {busy === `publish:${preview.version}` ? "Publishing…" : isLive ? "Published" : thread.liveVersion === null ? "Publish" : thread.liveVersion > preview.version ? `Roll back to v${preview.version}` : `Publish v${preview.version}`}
                </button>
              )}
            </div>
          </div>
          {note && (
            <div className={`bloom-note${note.error ? " error" : ""}`} role={note.error ? "alert" : "status"}>
              <span>{note.text}</span>
              {note.app && !note.error && <button type="button" className="link-btn" onClick={() => openApp(note.app!)}>Open the {kind}</button>}
              <CloseButton size="sm" aria-label="Dismiss" onClick={() => setNote(null)} />
            </div>
          )}
          <div className="bloom-preview-body">
            {pane === "cards" &&
              (cards ? (
                <CardPreviewsPanel thread={thread} room={room} host={cards} me={me} active={tab === "preview" || !isNarrow()} onPrompt={prompt} onShowInApp={showInApp} />
              ) : (
                <div className="bloom-empty"><p className="muted">Connecting…</p></div>
              ))}
            {pane === "cards" ? null : preview ? (
              <AppFrame
                key={preview.app.bloom?.bundle}
                app={preview.app}
                room={room}
                user={me}
                members={() => members.map((m) => ({ id: m.id, name: m.name, color: colorFor(m.id) }))}
                channelId={() => stream}
                route={route}
                openApp={(slug) => openApp(slug)}
              />
            ) : (
              <div className="bloom-empty">
                <div className="bloom-empty-icon" aria-hidden="true">{thread.icon}</div>
                <h2>{building ? `Bloom is building your ${kind}` : `Your ${kind} will appear here`}</h2>
                <p className="muted">{building ? "Every draft is checked before it shows up here, so this takes a minute." : thread.status === "asking" ? "Answer Bloom's questions in the chat to get the first draft." : "Describe what it should do in the chat, and Bloom builds a first draft."}</p>
                {building && (
                  <ol className="bloom-steps" aria-label="Progress">
                    {STEPS.map((s, i) => (
                      <li key={s.label} className={i < step ? "done" : i === step ? "now" : ""}>
                        <span className="bloom-step-mark" aria-hidden="true">{i < step ? "✓" : ""}</span>
                        <span className={i === step ? "shimmer" : undefined}>{s.label}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )}
            {preview && building && pane === "app" && (
              <div className="bloom-building" role="status">
                <span className="bloom-spinner" aria-hidden="true" />
                <span className="shimmer">{thread.progress ?? "Building"}</span>
                <span className="muted">· the next draft replaces this one when its checks pass</span>
              </div>
            )}
            {showVersions && (
              <VersionsPanel
                versions={versions}
                thread={thread}
                names={names}
                editor={editor}
                busy={busy}
                onClose={() => setShowVersions(false)}
                onPreview={(n) => void previewAt(n)}
                onPublish={(n) => void publish(n)}
                canDelete={mayDeleteVersion}
                onDelete={(n) => setDeleteVersionNum(n)}
              />
            )}
            {deleteVersionNum !== null && (
              <DeleteVersionConfirm
                n={deleteVersionNum}
                kind={deleteKind(versions ?? [], deleteVersionNum, thread.liveVersion)}
                onConfirm={() => {
                  const n = deleteVersionNum;
                  setDeleteVersionNum(null);
                  return deleteVersion(n);
                }}
                onClose={() => setDeleteVersionNum(null)}
              />
            )}
          </div>
        </section>
      </div>
    </main>
  );
}

function LockIcon({ locked }: { locked: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4.5" y="10.5" width="15" height="10" rx="2.5" />
      <path d={locked ? "M8 10.5V7.5a4 4 0 0 1 8 0v3" : "M8 10.5V7.5a4 4 0 0 1 7.6-1.7"} />
    </svg>
  );
}

/** Every version of the app: number, what changed, who asked and what they said, when; preview or publish one. */
function VersionsPanel({ versions, thread, names, editor, canDelete, busy, onClose, onPreview, onPublish, onDelete }: {
  versions: AppVersion[] | null;
  thread: BloomingThread;
  names: Map<string, string>;
  editor: boolean;
  canDelete?: boolean;
  busy: string | null;
  onClose: () => void;
  onPreview: (n: number) => void;
  onPublish: (n: number) => void;
  onDelete?: (n: number) => void;
}) {
  return (
    <aside className="bloom-versions" aria-label="Versions">
      <div className="bloom-versions-head">
        <strong>Versions</strong>
        <CloseButton size="sm" aria-label="Close versions" onClick={onClose} />
      </div>
      {!versions ? (
        <p className="muted bloom-versions-empty">Loading…</p>
      ) : (
        <VersionList versions={versions} live={thread.liveVersion} previewing={thread.preview?.version ?? null} names={names} editor={editor} canDelete={canDelete} busy={busy} onPreview={onPreview} onPublish={onPublish} onDelete={onDelete} />
      )}
    </aside>
  );
}

export function VersionList({ versions, live, previewing, names, editor, canDelete, busy, onPreview, onPublish, onDelete }: {
  versions: AppVersion[];
  live: number | null;
  previewing: number | null;
  names: Map<string, string>;
  editor: boolean;
  canDelete?: boolean;
  busy: string | null;
  onPreview?: (n: number) => void;
  onPublish: (n: number) => void;
  onDelete?: (n: number) => void;
}) {
  if (!versions.length) return <p className="muted bloom-versions-empty">No versions yet.</p>;
  return (
    <ol className="version-list">
      {versions.map((v) => {
        const isLive = v.n === live;
        const isDraft = !isLive && !v.publishedAt;
        return (
          <li key={v.n} className={`version${isLive ? " live" : ""}${v.n === previewing ? " previewing" : ""}`}>
            <div className="version-num" aria-hidden="true">{v.n}</div>
            <div className="version-text">
              <div className="version-title">
                <span>Version {v.n}</span>
                {isLive && <span className="bloom-tag live">Live</span>}
                {v.n === previewing && <span className="bloom-tag">In preview</span>}
              </div>
              <div className="version-summary">{v.summary}</div>
              <div className="version-meta muted">
                <Avatar id={v.authorId} name={names.get(v.authorId) ?? "?"} size={16} />
                <span>{names.get(v.authorId) ?? "Someone"}{v.via && <span className="version-via"> with {v.via}</span>}</span>
                <span aria-hidden="true">·</span>
                <time dateTime={new Date(v.at).toISOString()} title={when.format(v.at)}>{ago(v.at)}</time>
                {v.publishedAt && !isLive && <><span aria-hidden="true">·</span><span>was live</span></>}
              </div>
              {v.prompt && <blockquote className="version-prompt" title={v.prompt}>{v.prompt}</blockquote>}
            </div>
            <div className="version-actions">
              {onPreview && v.n !== previewing && (
                <button type="button" className="btn btn-small" onClick={() => onPreview(v.n)} disabled={busy !== null}>Preview</button>
              )}
              {editor && !isLive && (
                <button type="button" className="btn btn-small" onClick={() => onPublish(v.n)} disabled={busy !== null}>
                  {busy === `publish:${v.n}` ? "Publishing…" : live !== null && v.n < live ? "Roll back" : live !== null && v.publishedAt ? "Republish" : "Publish"}
                </button>
              )}
              {canDelete && onDelete && deleteKind(versions, v.n, live) !== null && (
                <button
                  type="button"
                  className="btn btn-small btn-danger"
                  onClick={() => onDelete(v.n)}
                  disabled={busy !== null}
                  title={isDraft ? `Delete draft ${v.n}` : `Delete version ${v.n}`}
                >
                  Delete
                </button>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

/** The app home's Blooming section: every thread where an app is being (or was) built. */
export function BloomingList({ threads, names, onOpen }: { threads: BloomingThread[]; names: Map<string, string>; onOpen: (id: string) => void }) {
  const sorted = useMemo(() => [...threads].sort((a, b) => b.updatedAt - a.updatedAt), [threads]);
  if (!sorted.length) return null;
  return (
    <section className="blooming-section" aria-label="Blooming">
      <h3 className="apps-section-title">Blooming</h3>
      <p className="muted apps-section-sub">Apps being built by chatting. Anyone can watch and join in.</p>
      <ul className="blooming-list">
        {sorted.map((t) => (
          <li key={t.id}>
            <button type="button" className="blooming-row" onClick={() => onOpen(t.id)}>
              <span className="app-icon small" aria-hidden="true">{t.icon}</span>
              <span className="blooming-row-text">
                <span className="blooming-row-title">
                  {t.title}
                  {t.liveVersion !== null && <span className="bloom-tag live">Live</span>}
                  {t.locked && <span className="bloom-tag">Locked</span>}
                </span>
                <span className="blooming-row-sub muted">
                  {names.get(t.ownerId) ?? "Someone"} · {t.drafts ? `${t.drafts} version${t.drafts === 1 ? "" : "s"}` : "no draft yet"} · {ago(t.updatedAt)}
                </span>
              </span>
              <BloomStatus thread={t} compact />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * "New app" or "New bot": apps and bots start from their own entry points (docs/bots.md). An app's thread goes in a
 * channel for everyone to watch; a bot's is always in my DM with it, where only I see it.
 */
export function NewAppDialog({ kind, channels, defaultChannel, onStart, onClose }: { kind: "app" | "bot"; channels: { id: string; name: string }[]; defaultChannel: string | null; onStart: (channelId: string, prompt: string, opts?: { bot?: boolean }) => Promise<string | null>; onClose: () => void }) {
  const bot = kind === "bot";
  const [prompt, setPrompt] = useState("");
  const [channelId, setChannelId] = useState(defaultChannel ?? channels[0]?.id ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    area.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, []);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!channelId && !bot) return;
    setBusy(true);
    setError(null);
    const err = await onStart(channelId, prompt, bot ? { bot: true } : undefined);
    setBusy(false);
    if (err) setError(err);
  };
  const ideas = bot
    ? ["Welcome people who join and point them to #general", "Answer “!roll” with a dice roll", "Star messages that get three ⭐ reactions"]
    : ["A packing list for the trip: who's bringing what", "A chore wheel for the house", "Movie night: suggest films and vote"];
  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <form className="modal new-app" role="dialog" aria-modal="true" aria-labelledby="new-app-title" onSubmit={(e) => void submit(e)}>
        <div className="new-app-head">
          <span className="new-app-mark" aria-hidden="true">{bot ? "🤖" : "🌱"}</span>
          <div>
            <h2 id="new-app-title">{bot ? "Make a new bot" : "Bloom a new app"}</h2>
            <p className="muted">
              {bot
                ? "Describe it, and Bloom builds it with you in a thread only you can see. Once it's published, it joins the server: you choose who talks to it and which channels it's in."
                : "Describe it, and Bloom builds it with you in a thread. Everyone can watch, try the preview and chip in."}
            </p>
          </div>
          <CloseButton onClick={onClose} aria-label="Close" />
        </div>
        <label className="label" htmlFor="new-app-prompt">What should it do?</label>
        <textarea
          id="new-app-prompt"
          ref={area}
          className="input new-app-prompt"
          rows={3}
          maxLength={2000}
          placeholder={bot ? "A bot that welcomes new people and answers questions about the server…" : "A packing list for the trip, where people claim what they'll bring…"}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) e.currentTarget.form?.requestSubmit();
          }}
        />
        <div className="new-app-ideas" aria-label="Ideas">
          {ideas.map((i) => (
            <button key={i} type="button" className="idea" onClick={() => setPrompt(i)}>{i}</button>
          ))}
        </div>
        {!bot && (
          <>
            <label className="label" htmlFor="new-app-channel">Blooming thread goes in</label>
            <span className="card-select">
              <select id="new-app-channel" value={channelId} onChange={(e) => setChannelId(e.target.value)}>
                {channels.map((c) => (
                  <option key={c.id} value={c.id}>#{c.name}</option>
                ))}
              </select>
            </span>
          </>
        )}
        {error && <p className="error" role="alert" style={{ margin: 0 }}>{error}</p>}
        <div className="new-app-foot">
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy || (!channelId && !bot)}>{busy ? "Starting…" : bot ? "Start building" : "Start blooming"}</button>
        </div>
      </form>
    </div>
  );
}

/** An app's About tab: what it is, who owns and edits it, its versions, and where it was bloomed. */
export function AppAbout({ app, room, me, members, names, thread, onOpenThread, onDeleted, channels = [], defaultChannel = null, bot = null, onMessageBot }: { app: AppManifest; room: Room; me: { id: string; name: string; role: Role }; members: Member[]; names: Map<string, string>; thread: BloomingThread | null; onOpenThread: (id: string) => void; onDeleted?: () => void; channels?: { id: string; name: string }[]; defaultChannel?: string | null; bot?: BotInfo | null; onMessageBot?: () => void }) {
  const b = app.bloom;
  const [versions, setVersions] = useState<AppVersion[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<{ text: string; error?: boolean } | null>(null);
  const [adding, setAdding] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deleteVersionNum, setDeleteVersionNum] = useState<number | null>(null);
  const load = () => void (b ? room.bloomVersions(b.thread).then((v) => setVersions(v ?? [])) : undefined);
  useEffect(load, [b?.thread, app.version]);
  // A bot: only its owner changes it (no editors, and server owners and admins can only switch it off).
  const isOwner = !!b && (me.id === b.owner || (me.role === "owner" && !app.bot));
  const editor = !!b && (app.bot ? me.id === b.owner : me.role !== "member" || me.id === b.owner || b.editors.includes(me.id));
  const mayDelete = canDeleteApp(app, me);
  const custom = app.exceptions?.length ? app.exceptions : null;
  const setEditors = async (editors: string[]) => {
    const r = await room.setAppEditors(app.slug, editors);
    setNote(r.ok ? null : { text: r.message, error: true });
  };
  const publish = async (n: number) => {
    if (!b) return;
    setBusy(`publish:${n}`);
    const r = await room.bloomPublish(b.thread, n);
    setBusy(null);
    setNote(r.ok ? { text: `Version ${n} is live.` } : { text: r.message, error: true });
    load();
  };
  const deleteVersion = async (n: number) => {
    if (!b) return;
    setBusy(`deleteVersion:${n}`);
    const r = await room.deleteVersion(b.thread, n);
    setBusy(null);
    if (!r.ok) setNote({ text: r.message ?? "Couldn't delete version", error: true });
    else {
      setNote({ text: `Version ${n} deleted.` });
      load();
    }
  };
  const owner = b ? (names.get(b.owner) ?? "Someone") : "Bloom";
  const candidates = members.filter((m) => b && m.id !== b.owner && !b.editors.includes(m.id) && m.role === "member");
  return (
    <div className="app-about">
      <section className="about-card">
        <div className="about-hero">
          <span className="app-icon" aria-hidden="true">{app.icon}</span>
          <div className="about-hero-text">
            <h2>{app.name}</h2>
            <p>{app.description}</p>
            <div className="about-tags">
              <span className="bloom-tag">/{app.slug}</span>
              {app.aliases?.map((a) => <span key={a} className="bloom-tag">/{a}</span>)}
              {b ? <span className="bloom-tag live">Version {app.version}</span> : <span className="bloom-tag">Made by Bloom's team</span>}
              {custom && <span className="bloom-tag custom" title="It goes beyond the standard kit">Custom: {custom.join(", ")}</span>}
            </div>
          </div>
        </div>
        {b && (
          <div className="about-thread">
            <span aria-hidden="true">🌱</span>
            <span>{thread ? <>Bloomed by <strong>{owner}</strong> in a blooming thread{b.publishedAt ? `, live since ${when.format(b.publishedAt)}` : ""}.</> : <>Bloomed by <strong>{owner}</strong>. Its blooming thread was deleted with its channel.</>}</span>
            {thread && <button type="button" className="btn btn-small" onClick={() => onOpenThread(thread.id)}>{editor ? "Change it with Bloom" : "Open its thread"}</button>}
          </div>
        )}
        {app.firstParty && me.role !== "member" && <EditWithBloom app={app} room={room} channels={channels} defaultChannel={defaultChannel} onDone={onOpenThread} />}
      </section>

      {bot && <BotAbout bot={bot} room={room} me={me} members={members} names={names} channels={channels} onMessage={() => onMessageBot?.()} />}

      <section className="about-card">
        <h3>Commands</h3>
        <ul className="about-commands">
          <li><code>/{app.slug}</code><span className="muted">Open {app.name}</span></li>
          {app.commands.map((c) => (
            <li key={c.name}><code>/{app.slug} {c.name}</code><span className="muted">{c.description}</span></li>
          ))}
        </ul>
      </section>

      {(app.integrations?.length || app.schedules?.length) ? <AboutOutside app={app} room={room} me={me} /> : null}

      {b && !app.bot && (
        <section className="about-card">
          <h3>Who can do what</h3>
          <ul className="about-roles">
            <li>
              <span className="about-role">Owner</span>
              <span className="about-people"><Avatar id={b.owner} name={owner} size={22} /> {owner}</span>
              <span className="muted about-role-can">Publishes, rolls back, chooses editors</span>
            </li>
            <li>
              <span className="about-role">Editors</span>
              <span className="about-people">
                {b.editors.map((id) => (
                  <span key={id} className="about-person">
                    <Avatar id={id} name={names.get(id) ?? "?"} size={22} /> {names.get(id) ?? "Someone"}
                    {isOwner && <button type="button" className="icon-btn about-remove" aria-label={`Remove ${names.get(id) ?? "editor"}`} onClick={() => void setEditors(b.editors.filter((x) => x !== id))}>×</button>}
                  </span>
                ))}
                <span className="muted">{b.editors.length ? "and server owners and admins" : "Server owners and admins"}</span>
              </span>
              <span className="muted about-role-can">Prompt changes, publish, restore data</span>
            </li>
            <li>
              <span className="about-role">Members</span>
              <span className="about-people muted">Everyone in the server</span>
              <span className="muted about-role-can">Use it and its cards</span>
            </li>
          </ul>
          {isOwner && candidates.length > 0 && (
            <form className="about-add" onSubmit={(e) => { e.preventDefault(); if (adding) void setEditors([...b.editors, adding]).then(() => setAdding("")); }}>
              <span className="card-select">
                <select aria-label="Add an editor" value={adding} onChange={(e) => setAdding(e.target.value)}>
                  <option value="">Add an editor…</option>
                  {candidates.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </span>
              <button type="submit" className="btn btn-small" disabled={!adding}>Add</button>
            </form>
          )}
        </section>
      )}

      {b && (
        <section className="about-card">
          <h3>Versions</h3>
          <p className="muted about-help">Each change made in the blooming thread is a version. Publishing an older one rolls the app back (its data stays as it is).</p>
          {note && <div className={`bloom-note${note.error ? " error" : ""}`} role={note.error ? "alert" : "status"}><span>{note.text}</span></div>}
          {versions ? (
            <VersionList
              versions={versions}
              live={app.version}
              previewing={null}
              names={names}
              editor={editor}
              canDelete={mayDelete}
              busy={busy}
              onPublish={(n) => void publish(n)}
              onDelete={(n) => setDeleteVersionNum(n)}
            />
          ) : (
            <p className="muted">Loading…</p>
          )}
          {deleteVersionNum !== null && (
            <DeleteVersionConfirm
              n={deleteVersionNum}
              kind={deleteKind(versions ?? [], deleteVersionNum, app.version ?? null)}
              onConfirm={() => {
                const n = deleteVersionNum;
                setDeleteVersionNum(null);
                return deleteVersion(n);
              }}
              onClose={() => setDeleteVersionNum(null)}
            />
          )}
        </section>
      )}

      {mayDelete && (
        <section className="about-card about-danger">
          <h3>Delete app</h3>
          <p className="muted about-help">Permanently delete this app, its data, and its blooming thread. This cannot be undone.</p>
          <button type="button" className="btn btn-danger" onClick={() => setDeleting(true)}>
            Delete {app.name}
          </button>
          {deleting && (
            <Confirm
              title={`Delete ${app.name}?`}
              confirmLabel="Delete app"
              danger
              onConfirm={async () => {
                const r = await room.deleteApp(app.slug);
                if (!r.ok) return r.message ?? "Couldn't delete app";
                onDeleted?.();
                return null;
              }}
              onClose={() => setDeleting(false)}
            >
              <p className="muted">This will permanently delete <strong>{app.name}</strong>, its blooming thread, and all of its data. This cannot be undone.</p>
            </Confirm>
          )}
        </section>
      )}
    </div>
  );
}

const INTEGRATION_NAMES: Record<string, { name: string; icon: string }> = Object.fromEntries(INTEGRATIONS.map((i) => [i.id, { name: i.name, icon: i.icon }]));

/** About → Outside data: the integrations an app reads from (and whether they're on here), and its schedules. */
function AboutOutside({ app, room, me }: { app: AppManifest; room: Room; me: { id: string; name: string; role: Role } }) {
  const [info, setInfo] = useState<{ schedules: { action: string; everyMinutes: number; nextAt: number; paused: boolean; lastError?: string; lastRun?: number }[]; usage: { integration: string; calls: number; cached: number; errors: number }[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const admin = me.role === "owner" || me.role === "admin";
  const load = () => void api<NonNullable<typeof info>>(`/servers/${room.serverId}/apps/${app.slug}/integrations`).then(setInfo, (e: Error) => setError(e.message));
  useEffect(load, [app.slug, app.version]);
  const settings = room.getSnapshot().integrations;
  const pause = async (paused: boolean) => {
    setError(null);
    try {
      const r = await api<{ schedules: NonNullable<typeof info>["schedules"] }>(`/servers/${room.serverId}/apps/${app.slug}/schedules`, { method: "PUT", body: { paused } });
      setInfo((i) => (i ? { ...i, schedules: r.schedules } : i));
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const every = (m: number) => (m % 60 === 0 ? (m === 60 ? "every hour" : `every ${m / 60} hours`) : `every ${m} minutes`);
  return (
    <section className="about-card about-outside">
      <h3>Outside data</h3>
      {!!app.integrations?.length && (
        <ul className="about-commands">
          {app.integrations.map((id) => {
            const s = settings.find((x) => x.id === id);
            const n = INTEGRATION_NAMES[id] ?? { name: id, icon: "◆" };
            const used = info?.usage.find((u) => u.integration === id);
            return (
              <li key={id}>
                <span>{n.icon} <strong>{n.name}</strong></span>
                <span className="muted">
                  {s?.ready ? "On in this server" : s?.enabled ? "Needs a key: an owner or admin can add it in Apps → Integrations" : "Off: an owner or admin can turn it on in Apps → Integrations"}
                  {used && used.calls + used.cached > 0 ? ` · ${used.calls + used.cached} lookups this week` : ""}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {!!app.schedules?.length && (
        <ul className="about-commands">
          {app.schedules.map((sc) => {
            const live = info?.schedules.find((x) => x.action === sc.action);
            return (
              <li key={sc.action}>
                <span>⏱ <strong>{app.actions[sc.action]?.description ?? sc.action}</strong></span>
                <span className="muted">
                  {b(app) ? `Bloom runs it ${every(sc.everyMinutes)}` : "Runs on a schedule once published"}
                  {live?.paused ? ` · paused${live.lastError ? `: ${live.lastError}` : ""}` : live?.lastRun ? ` · last ran ${when.format(live.lastRun)}` : ""}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {admin && !!info?.schedules.length && (
        <div>
          <button type="button" className="btn btn-small" onClick={() => void pause(!info.schedules.every((x) => x.paused))}>
            {info.schedules.every((x) => x.paused) ? "Resume schedules" : "Pause schedules"}
          </button>
        </div>
      )}
      {error && <p className="error" role="alert">{error}</p>}
    </section>
  );
}

const b = (app: AppManifest) => !!app.bloom && !app.bloom.draft;

/**
 * A built-in app's "Edit with Bloom" (owners and admins): moves it into a blooming thread in a channel they pick, so
 * it changes by chatting like any app. Its data and History stay; it can't be moved back.
 */
function EditWithBloom({ app, room, channels, defaultChannel, onDone }: { app: AppManifest; room: Room; channels: { id: string; name: string }[]; defaultChannel: string | null; onDone: (threadId: string) => void }) {
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState(defaultChannel ?? channels[0]?.id ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const go = async () => {
    if (!channel) return;
    setBusy(true);
    setError(null);
    const r = await room.bloomAdopt(app.slug, channel);
    setBusy(false);
    if (r.ok) onDone(r.threadId);
    else setError(r.message);
  };
  if (!open) {
    return (
      <div className="about-thread about-adopt">
        <span aria-hidden="true">🌱</span>
        <span>{app.name} is built into Bloom, so it can't be changed by chatting yet. Move it into a blooming thread to change it like any app.</span>
        <button type="button" className="btn btn-small" onClick={() => setOpen(true)}>Edit with Bloom</button>
      </div>
    );
  }
  return (
    <form className="about-thread about-adopt open" onSubmit={(e) => { e.preventDefault(); void go(); }}>
      <span aria-hidden="true">🌱</span>
      <span>
        <strong>Move {app.name} into a blooming thread?</strong> Every request, vote and bit of History stays, and /{app.slug} works as before. From then on anyone who can prompt the thread can propose changes, and owners and admins publish them. This can't be undone.
      </span>
      <span className="about-adopt-row">
        <span className="card-select">
          <select aria-label="Channel for its blooming thread" value={channel} onChange={(e) => setChannel(e.target.value)} disabled={busy}>
            {channels.map((c) => <option key={c.id} value={c.id}>#{c.name}</option>)}
          </select>
        </span>
        <button type="submit" className="btn btn-small btn-primary" disabled={busy || !channel}>{busy ? "Moving…" : "Move it"}</button>
        <button type="button" className="btn btn-small" onClick={() => { setOpen(false); setError(null); }} disabled={busy}>Cancel</button>
      </span>
      {error && <span className="bloom-note error about-adopt-error" role="alert">{error}</span>}
    </form>
  );
}
